
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Upload, CheckCircle, AlertCircle } from 'lucide-react';

interface PhotoData {
  fileName: string;
  title: string;
  variableName: string;
  description: string;
  altTag: string;
  keywords: string[];
  useCase: string;
}

const BulkPhotoUploader = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'completed' | 'error'>('idle');

  const photosData: PhotoData[] = [
    {
      fileName: 'damaged-roof-shingles.png',
      title: 'Storm Damaged Roof Shingles',
      variableName: 'storm_damage_shingles',
      description: 'Residential roof showing storm damage with missing and damaged shingles, exposed underlayment, and debris.',
      altTag: 'Storm damaged roof with missing shingles and exposed underlayment',
      keywords: ['storm damage', 'roof repair', 'missing shingles', 'roof damage', 'residential roofing'],
      useCase: 'Insurance claims, storm damage assessment, before photos'
    },
    {
      fileName: 'workers-on-roof-repair.png',
      title: 'Professional Roof Installation Team',
      variableName: 'roof_installation_team',
      description: 'Carolina Trophy Roofs professional team installing new roofing materials on residential home.',
      altTag: 'Professional roofers installing new roof on residential home',
      keywords: ['roof installation', 'professional roofers', 'residential roofing', 'team work', 'roofing contractors'],
      useCase: 'Service showcase, team professionalism, installation process'
    },
    {
      fileName: 'carolina-trophy-roofs-sign.png',
      title: 'Carolina Trophy Roofs Company Sign',
      variableName: 'company_signage',
      description: 'Professional Carolina Trophy Roofs yard sign displaying company branding and contact information.',
      altTag: 'Carolina Trophy Roofs professional yard sign with company logo and phone number',
      keywords: ['Carolina Trophy Roofs', 'company sign', 'professional branding', 'roofing contractor', 'Eastern NC'],
      useCase: 'Brand recognition, professional presence, company marketing'
    },
    {
      fileName: 'winter-roof-project.png',
      title: 'Winter Roof Replacement Project',
      variableName: 'winter_roof_project',
      description: 'Large residential home undergoing complete roof replacement during winter months with professional installation.',
      altTag: 'Large residential home with new roof installation in winter',
      keywords: ['winter roofing', 'roof replacement', 'large home', 'residential roofing', 'complete replacement'],
      useCase: 'Winter services, large project showcase, residential replacement'
    },
    {
      fileName: 'office-interior.png',
      title: 'Carolina Trophy Roofs Office Interior',
      variableName: 'office_interior',
      description: 'Professional office interior of Carolina Trophy Roofs showing awards, certifications, and customer service area.',
      altTag: 'Carolina Trophy Roofs professional office interior with awards and customer service desk',
      keywords: ['office interior', 'professional office', 'awards', 'certifications', 'customer service'],
      useCase: 'Company credibility, professional image, awards showcase'
    },
    {
      fileName: 'drainage-work.png',
      title: 'Roof Drainage System Installation',
      variableName: 'drainage_installation',
      description: 'Professional installation of roof drainage system with proper trenching and pipe placement.',
      altTag: 'Professional roof drainage system installation with trenching work',
      keywords: ['drainage system', 'roof drainage', 'professional installation', 'trenching', 'water management'],
      useCase: 'Drainage services, water management solutions, professional installation'
    },
    {
      fileName: 'aerial-roof-view.png',
      title: 'Aerial View of Completed Roof',
      variableName: 'aerial_roof_completed',
      description: 'Aerial drone view of completed residential roof installation showing quality workmanship and professional results.',
      altTag: 'Aerial drone view of completed residential roof installation',
      keywords: ['aerial view', 'completed roof', 'drone photography', 'quality workmanship', 'finished project'],
      useCase: 'Project completion showcase, aerial photography, quality demonstration'
    },
    {
      fileName: 'roof-vent-installation.png',
      title: 'Professional Roof Vent Installation',
      variableName: 'vent_installation',
      description: 'Carolina Trophy Roofs team installing roof vents with proper techniques and safety equipment.',
      altTag: 'Professional roofers installing roof vents with safety equipment',
      keywords: ['roof vents', 'professional installation', 'safety equipment', 'ventilation', 'roofing team'],
      useCase: 'Ventilation services, professional techniques, safety standards'
    },
    {
      fileName: 'roof-inspection.png',
      title: 'Detailed Roof Inspection Process',
      variableName: 'roof_inspection',
      description: 'Professional roof inspector conducting detailed assessment of roofing materials and condition.',
      altTag: 'Professional roof inspector examining shingle condition during inspection',
      keywords: ['roof inspection', 'professional inspector', 'roof assessment', 'detailed inspection', 'roofing evaluation'],
      useCase: 'Inspection services, professional assessment, maintenance evaluation'
    },
    {
      fileName: 'team-meeting.png',
      title: 'Carolina Trophy Roofs Team Meeting',
      variableName: 'team_meeting',
      description: 'Carolina Trophy Roofs professional team meeting in conference room discussing project plans and customer service.',
      altTag: 'Carolina Trophy Roofs team members in professional meeting discussing projects',
      keywords: ['team meeting', 'professional team', 'project planning', 'customer service', 'Carolina Trophy Roofs'],
      useCase: 'Team professionalism, company culture, project planning'
    }
  ];

  const uploadPhotos = async () => {
    setIsUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);

    try {
      let successCount = 0;
      const totalPhotos = photosData.length;

      for (let i = 0; i < photosData.length; i++) {
        const photo = photosData[i];
        
        try {
          // Fetch the image from the public folder
          const response = await fetch(`/roofing-photos/${photo.fileName}`);
          if (!response.ok) throw new Error(`Failed to fetch ${photo.fileName}`);
          
          const blob = await response.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));

          // Upload via the generate-image function
          const { data, error } = await supabase.functions.invoke('generate-image', {
            body: {
              imageData: base64,
              fileName: photo.fileName,
              mimeType: blob.type,
              title: photo.title,
              variableName: photo.variableName,
              description: photo.description,
              altTag: photo.altTag,
              metaDescription: photo.description,
              keywords: photo.keywords,
              useCase: photo.useCase
            }
          });

          if (error) throw error;
          
          successCount++;
          setUploadProgress(Math.round((successCount / totalPhotos) * 100));
          
          console.log(`Successfully uploaded: ${photo.title}`);
          
        } catch (photoError) {
          console.error(`Error uploading ${photo.fileName}:`, photoError);
        }
      }

      if (successCount === totalPhotos) {
        setUploadStatus('completed');
        toast.success(`Successfully uploaded all ${successCount} photos to the album!`);
      } else {
        toast.success(`Uploaded ${successCount} of ${totalPhotos} photos. Check console for any errors.`);
      }

    } catch (error) {
      console.error('Bulk upload error:', error);
      setUploadStatus('error');
      toast.error('Failed to upload photos. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Bulk Photo Upload
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="text-sm text-gray-600">
          Ready to upload {photosData.length} Carolina Trophy Roofs project photos to your album.
        </div>

        {uploadStatus === 'uploading' && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Uploading photos...</div>
            <Progress value={uploadProgress} className="w-full" />
            <div className="text-xs text-gray-500">{uploadProgress}% complete</div>
          </div>
        )}

        {uploadStatus === 'completed' && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm">All photos uploaded successfully!</span>
          </div>
        )}

        {uploadStatus === 'error' && (
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">Upload failed. Please try again.</span>
          </div>
        )}

        <div className="space-y-2">
          <div className="text-sm font-medium">Photos to upload:</div>
          <div className="text-xs text-gray-500 max-h-32 overflow-y-auto">
            {photosData.map((photo, index) => (
              <div key={index} className="py-1">
                • {photo.title}
              </div>
            ))}
          </div>
        </div>

        <Button 
          onClick={uploadPhotos} 
          disabled={isUploading || uploadStatus === 'completed'}
          className="w-full"
        >
          {isUploading ? 'Uploading...' : uploadStatus === 'completed' ? 'Upload Complete' : 'Upload All Photos'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default BulkPhotoUploader;
